import React, { useEffect, useState } from "react";
import {
  Archive,
  CalendarClock,
  ChevronDown,
  ChevronsRight,
  FlaskConical,
  GitCompare,
  Handshake,
  HelpCircle,
  Link2,
  ListPlus,
  Loader2,
  Map,
  MessageCircle,
  Minimize2,
  Network,
  PauseCircle,
  Pin,
  PlayCircle,
  Puzzle,
  RefreshCcw,
  Rocket,
  Scale,
  Scissors,
  Search,
  Shield,
  ShieldAlert,
  Shuffle,
  Sparkles,
  Square,
  Crosshair,
  Combine,
  ListChecks,
  Tag,
  Telescope,
  Volume2,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { useTranslation } from "react-i18next";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  LINK_TYPES,
  MmMenu,
  MmObject,
  MmRegister,
  OPERATOR_META,
  listObjects,
  operatorAssist,
} from "../../api/mentalModels";
import { useMmText } from "../../hooks/useMmText";

/**
 * Operator menu + interaction archetypes for the phase-1 operator set.
 * The server's menu is authoritative; this component only renders what the
 * visit's menu allows and collects the operator's input/output payloads.
 */

const OPERATOR_FALLBACKS: Record<string, { title: string; help: string; done: string }> = {
  name: {
    title: "Give it a name",
    help: "A name with texture turns weather into a thing you can hold.",
    done: "Name it",
  },
  reframe: {
    title: "Try another lens",
    help: "Same facts, different frame. Pick one that's already 10% true.",
    done: "Adopt this frame",
  },
  bound: {
    title: "Draw the edges",
    help: "What is explicitly NOT part of this? The excluded list does the work.",
    done: "Set boundaries",
  },
  split: {
    title: "Split it apart",
    help: "Cleave it where 'I know this' turns into 'I fear this'.",
    done: "Split",
  },
  concretize: {
    title: "Make it concrete",
    help: "Would you know unambiguously if it happened? If not, go one level down.",
    done: "Lock it in",
  },
  decompose: {
    title: "Break it down",
    help: "Find the one part small enough to start this week.",
    done: "Keep these parts",
  },
  question: {
    title: "Question it",
    help: "Aim at the weakest claim, not the whole thing.",
    done: "Save my answers",
  },
  doubt: {
    title: "Check the load-bearing premise",
    help: "Airtight despair usually has one soft premise. Find it.",
    done: "Record verdict",
  },
  test: {
    title: "Reality-check it",
    help: "Design the cheapest probe that could LOSE. It becomes an action item.",
    done: "Commit to the test",
  },
  park: {
    title: "Park it",
    help: "Not actionable now. Set a re-entry condition so it isn't suppression.",
    done: "Park it",
  },
  distance: {
    title: "Step back from it",
    help: "'I'm having the thought that…' — no content change, stance only.",
    done: "I see it as a thought",
  },
  retire: {
    title: "Retire it",
    help: "This belief did a job once. What takes its job now?",
    done: "Retire it",
  },
  compare: {
    title: "Compare it",
    help: "Against something real and specified — not an imagined ideal.",
    done: "Save the comparison",
  },
  link: {
    title: "Connect it",
    help: "Name the relation to another thought: causes, blocks, protects…",
    done: "Create the link",
  },
  map: {
    title: "See the territory",
    help: "Lay out the connected thoughts — chokepoints and gaps become visible.",
    done: "Save what you see",
  },
  simplify: {
    title: "Boil it down",
    help: "One essential dimension. Note what you're setting aside.",
    done: "Keep the essence",
  },
  elaborate: {
    title: "Add nuance",
    help: "Under what conditions is it more true? Less true?",
    done: "Save the nuance",
  },
  contextualize: {
    title: "Trace where it came from",
    help: "When did this form, under whom, solving what problem?",
    done: "Place it in time",
  },
  deduce: {
    title: "Follow it forward",
    help: "If this is true, then what? Stop at an action or a contradiction.",
    done: "Save the chain",
  },
  simulate: {
    title: "Run it in your head",
    help: "Day-one-hour-one specifics, not general feelings.",
    done: "Save what you saw",
  },
  analogize: {
    title: "Borrow a map",
    help: "How does a domain you know solve this shape of problem?",
    done: "Use this analogy",
  },
  accept_oppose: {
    title: "Accept or oppose",
    help: "Stop fighting what you can't change; enlist against what you can.",
    done: "Take this stance",
  },
  own_disown: {
    title: "Whose voice is this?",
    help: "If it plays in someone else's voice, it was their model.",
    done: "Save the answer",
  },
  // ---- design family (ICDF) ----
  dsn_research: {
    title: "Gather what's known",
    help: "Before designing, collect what you and the world already know about this.",
    done: "Save the findings",
  },
  dsn_define: {
    title: "Name the problem and the goal",
    help: "One sentence for what's wrong, one for what 'solved' looks like.",
    done: "Set the definition",
  },
  dsn_criteria: {
    title: "What would 'good' satisfy?",
    help: "The criteria a worthy design must meet — before falling in love with one.",
    done: "Save the criteria",
  },
  dsn_generate: {
    title: "Generate options",
    help: "At least three genuinely different ways this could go.",
    done: "Keep these options",
  },
  dsn_synthesize: {
    title: "Compare and combine",
    help: "Score the options against your criteria — then steal the best parts of each.",
    done: "Save the synthesis",
  },
  dsn_mental_test: {
    title: "Run it in the mind",
    help: "Walk an ordinary day with this design in place. Where does it creak?",
    done: "Save the test run",
  },
  dsn_harden: {
    title: "Strengthen the weak points",
    help: "Take each creak from the test and give the design an answer to it.",
    done: "Harden it",
  },
  dsn_premortem: {
    title: "It failed — why?",
    help: "Jump to the future where it flopped. Write the honest autopsy now.",
    done: "Save the premortem",
  },
  dsn_implement: {
    title: "Commit it to reality",
    help: "The design leaves your head: name the first concrete step and when.",
    done: "Commit",
  },
  amplify: {
    title: "Strengthen it",
    help: "Re-inhabit one specific memory that proves it. Receipts, not slogans.",
    done: "Anchor it",
  },
};

/** Calm accent per operator family — a dot on each card, never the whole card. */
const FAMILY_ACCENTS: Record<string, string> = {
  create: "bg-sky-400",
  form: "bg-amber-400",
  relations: "bg-violet-400",
  epistemic: "bg-emerald-400",
  affect: "bg-rose-400",
  design: "bg-indigo-400",
};

const FAMILY_TINTS: Record<string, { bg: string; fg: string }> = {
  create: { bg: "bg-sky-50", fg: "text-sky-600" },
  form: { bg: "bg-amber-50", fg: "text-amber-600" },
  relations: { bg: "bg-violet-50", fg: "text-violet-600" },
  epistemic: { bg: "bg-emerald-50", fg: "text-emerald-600" },
  affect: { bg: "bg-rose-50", fg: "text-rose-500" },
  design: { bg: "bg-indigo-50", fg: "text-indigo-600" },
};

/** Per-operator visual + preview copy: what you'd do, and what tends to move. */
const OPERATOR_VISUALS: Record<
  string,
  { icon: React.ElementType; example: string; effect: string }
> = {
  name: { icon: Tag, example: "“the Sunday audit”", effect: "less heavy · clearer" },
  reframe: { icon: RefreshCcw, example: "“feedback = free consulting”", effect: "lighter · more open" },
  bound: { icon: Square, example: "“the next 6 months — not my whole career”", effect: "smaller · more workable" },
  split: { icon: Scissors, example: "“boss's tone” vs “my job security”", effect: "untangled into parts" },
  retire: { icon: Archive, example: "“formed at 15 — no longer holds”", effect: "let go · replaced" },
  concretize: { icon: Pin, example: "“email Dana by Friday”", effect: "clearer · doable" },
  simplify: { icon: Minimize2, example: "“one question: does it grow me?”", effect: "less overwhelming" },
  elaborate: { icon: ListPlus, example: "“when is it more true? less true?”", effect: "more honest certainty" },
  decompose: { icon: Puzzle, example: "“one repaired relationship first”", effect: "smaller steps" },
  question: { icon: HelpCircle, example: "“what would count as evidence?”", effect: "less certain · more open" },
  doubt: { icon: Scale, example: "“does switching really take 10 years?”", effect: "certainty rechecked" },
  deduce: { icon: ChevronsRight, example: "“if true, next quarter is prep time”", effect: "consequences visible" },
  test: { icon: FlaskConical, example: "“three offer conversations in two weeks”", effect: "reality decides" },
  simulate: { icon: PlayCircle, example: "“it's 18 months later and it failed — why?”", effect: "the future gets sharper" },
  park: { icon: PauseCircle, example: "“revisit after the reorg announcement”", effect: "off your mind for now" },
  distance: { icon: Telescope, example: "“I'm having the thought that…”", effect: "less fused · calmer" },
  analogize: { icon: Map, example: "“career change as a system migration”", effect: "borrowed clarity" },
  accept_oppose: { icon: Handshake, example: "“accept the market — oppose my prep”", effect: "energy redirected" },
  own_disown: { icon: MessageCircle, example: "“that was my mentor's voice”", effect: "whose voice, known" },
  amplify: { icon: Volume2, example: "“relive the day it went right”", effect: "stronger · more present" },
  contextualize: { icon: CalendarClock, example: "“formed at my first job, under that manager”", effect: "less absolute · movable" },
  compare: { icon: GitCompare, example: "“this job vs. the actual offer — on 5 criteria”", effect: "hidden criteria surface" },
  link: { icon: Link2, example: "“my procrastination protects me from being seen”", effect: "connections named" },
  map: { icon: Network, example: "“every path routes through one belief”", effect: "the territory visible" },
  // ---- design family (ICDF) ----
  dsn_research: { icon: Search, example: "“what's already known about this?”", effect: "grounded in the known" },
  dsn_define: { icon: Crosshair, example: "“the problem is X; done means Y”", effect: "problem + goal named" },
  dsn_criteria: { icon: ListChecks, example: "“cheap, reversible, starts this week”", effect: "what 'good' means" },
  dsn_generate: { icon: Shuffle, example: "“three ways this could work”", effect: "options on the table" },
  dsn_synthesize: { icon: Combine, example: "“option A's core + option C's safety”", effect: "the best of each" },
  dsn_mental_test: { icon: PlayCircle, example: "“walk a Tuesday with this design”", effect: "run before building" },
  dsn_harden: { icon: Shield, example: "“what breaks it? patch that”", effect: "weak points covered" },
  dsn_premortem: { icon: ShieldAlert, example: "“it's a year on and it failed — why?”", effect: "failure met early" },
  dsn_implement: { icon: Rocket, example: "“first step on the calendar”", effect: "reality takes over" },
};

/**
 * Checklist-board archetype config (design family): the AI proposes items,
 * the user keeps/edits/extends them. `primary` is the item's main text field
 * in the assist payload; kept items go out under `outputKey` unchanged so the
 * biography and later design steps see the full structure.
 */
const CHECKLIST_CONFIG: Record<
  string,
  { assistKey: string; primary: string; secondary?: string; tertiary?: string; outputKey: string }
> = {
  dsn_criteria: { assistKey: "criteria", primary: "name", secondary: "why", outputKey: "criteria" },
  dsn_generate: {
    assistKey: "options",
    primary: "name",
    secondary: "sketch",
    tertiary: "tradeoff",
    outputKey: "options",
  },
  dsn_harden: { assistKey: "patches", primary: "patch", secondary: "weakPoint", outputKey: "patches" },
  dsn_premortem: { assistKey: "causes", primary: "cause", secondary: "earlySign", outputKey: "causes" },
};

/** Guided-steps archetype config: AI questions, one answer field each. */
const GUIDED_STEPS_CONFIG: Record<
  string,
  { assistKey: string; prompt: string; sub?: string; outputKey: string; answerKey: string; promptOutKey: string }
> = {
  dsn_research: {
    assistKey: "unknowns",
    prompt: "question",
    sub: "whyItMatters",
    outputKey: "findings",
    answerKey: "answer",
    promptOutKey: "question",
  },
  dsn_mental_test: {
    assistKey: "scenes",
    prompt: "scene",
    sub: "question",
    outputKey: "creaks",
    answerKey: "creak",
    promptOutKey: "scene",
  },
};

interface ApplyArgs {
  operatorId: string;
  input?: any;
  output?: any;
}

interface Props {
  object: MmObject;
  menu: MmMenu;
  warnings: string[];
  register: MmRegister;
  plannedRoute?: Array<{ operatorId: string; status: string }>;
  applying: boolean;
  /** Operator id requested from outside (e.g. a stepper chip click). */
  requestOperator?: string | null;
  onRequestConsumed?: () => void;
  /** Guided embeds: never show the operator menu — steps open via requestOperator. */
  hideMenu?: boolean;
  /** ICDF: operators this user has never applied — get a gentle "new to you" tag. */
  newOperatorIds?: string[];
  /**
   * Model page layout (spec 2026-11-03 model page §A3): the heavy note as a
   * gentle line, one suggested move card, the rest behind "Other moves (n)".
   */
  pageLayout?: boolean;
  onApply: (args: ApplyArgs) => void;
  onPark: (parkedCondition: string) => void;
  onRetire: (retiredNote: string) => void;
}

const MmOperatorFlow: React.FC<Props> = ({
  object,
  menu,
  warnings,
  register,
  plannedRoute,
  applying,
  requestOperator,
  onRequestConsumed,
  hideMenu,
  newOperatorIds,
  pageLayout,
  onApply,
  onPark,
  onRetire,
}) => {
  const { tm } = useMmText(register);
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();

  const [activeOperator, setActiveOperator] = useState<string | null>(null);
  const [assist, setAssist] = useState<any>(null);
  const [assistLoading, setAssistLoading] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [chosen, setChosen] = useState<number | null>(null);
  const [selectedParts, setSelectedParts] = useState<Record<number, boolean>>({});
  // compare_grid
  const [compareTarget, setCompareTarget] = useState("");
  const [compareRows, setCompareRows] = useState<Array<{ name: string; a: number; b: number }>>([]);
  // link_picker / territory_map
  const [otherObjects, setOtherObjects] = useState<MmObject[]>([]);
  const [selectedLinkTarget, setSelectedLinkTarget] = useState<string | null>(null);
  const [selectedLinkType, setSelectedLinkType] = useState<string>("relate_link");
  // design family archetypes
  const [stepAnswers, setStepAnswers] = useState<Record<number, string>>({});
  const [defineProblem, setDefineProblem] = useState("");
  const [defineDone, setDefineDone] = useState("");
  const [matrixScores, setMatrixScores] = useState<Record<string, Record<string, number>>>({});
  const [commitWhen, setCommitWhen] = useState("");
  const [otherMovesOpen, setOtherMovesOpen] = useState(false);

  const reset = () => {
    setActiveOperator(null);
    setAssist(null);
    setFreeText("");
    setChosen(null);
    setSelectedParts({});
    setStepAnswers({});
    setDefineProblem("");
    setDefineDone("");
    setMatrixScores({});
    setCommitWhen("");
  };

  // Stepper chips (and other outside affordances) can start an operator too
  useEffect(() => {
    if (requestOperator && menu.allowed.includes(requestOperator) && !activeOperator) {
      startOperator(requestOperator);
    }
    if (requestOperator) onRequestConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestOperator]);

  const startOperator = async (operatorId: string) => {
    setActiveOperator(operatorId);
    setAssist(null);
    setFreeText("");
    setChosen(null);
    setSelectedParts({});
    setCompareTarget("");
    setCompareRows([]);
    setSelectedLinkTarget(null);
    setSelectedLinkType("causes");
    setStepAnswers({});
    setDefineProblem("");
    setDefineDone("");
    setMatrixScores({});
    setCommitWhen("");
    const meta = OPERATOR_META[operatorId];
    // link/map need the user's other objects for picking / territory rendering
    if (operatorId === "link" || operatorId === "map") {
      try {
        const res = await listObjects(retryFetch, { status: "all" });
        setOtherObjects(res.objects.filter((o) => o._id !== object._id));
      } catch {
        setOtherObjects([]);
      }
    }
    // compare defers AI until the user names the comparison target
    if (operatorId === "compare") return;
    if (meta?.aiAssist) {
      setAssistLoading(true);
      try {
        const res = await operatorAssist(retryFetch, {
          operatorId,
          fiObject: object._id,
          language: i18n.language?.split("-")[0] || "en",
          register,
        });
        setAssist(res.assist);
        if (operatorId === "split" || operatorId === "decompose") {
          const parts = res.assist.members || res.assist.parts || [];
          const all: Record<number, boolean> = {};
          parts.forEach((_: any, i: number) => (all[i] = true));
          setSelectedParts(all);
        }
        // checklist boards start with every AI proposal kept — unticking is
        // the user's editorial act
        if (CHECKLIST_CONFIG[operatorId]) {
          const items = res.assist[CHECKLIST_CONFIG[operatorId].assistKey] || [];
          const all: Record<number, boolean> = {};
          items.forEach((_: any, i: number) => (all[i] = true));
          setSelectedParts(all);
        }
      } catch {
        setAssist(null); // archetypes degrade to manual input
      } finally {
        setAssistLoading(false);
      }
    }
  };

  const submit = (input: any, output: any) => {
    if (!activeOperator) return;
    onApply({ operatorId: activeOperator, input, output });
    reset();
  };

  // ----- archetype renderers -------------------------------------------------

  const renderGenerateChoose = () => {
    const candidates: any[] = assist?.candidates || [];
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    return (
      <div className="space-y-2">
        {candidates.map((c, i) => (
          <button
            key={i}
            onClick={() => setChosen(i)}
            className={cn(
              "w-full text-start rounded-lg border p-3 text-sm transition-colors",
              chosen === i ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
            )}
          >
            <span className="font-medium">{c.name || c.frame}</span>
            {c.statement && <p className="text-muted-foreground mt-1">{c.statement}</p>}
            {c.tenPercentTrue && (
              <p className="text-xs text-emerald-700 mt-1">
                {tm("ten_percent_true", "Already a little true:")} {c.tenPercentTrue}
              </p>
            )}
            {c.why && <p className="text-xs text-muted-foreground mt-1">{c.why}</p>}
          </button>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <input
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder={tm("or_your_own", "…or write your own")}
            className="flex-1 rounded-md border border-border px-3 py-2 text-sm bg-background"
          />
        </div>
        <Button
          disabled={applying || (chosen === null && !freeText.trim())}
          onClick={() => {
            const picked = chosen !== null ? candidates[chosen] : null;
            submit(
              { candidates, source: picked ? "ai" : "user" },
              picked
                ? activeOperator === "name"
                  ? { chosenName: picked.name }
                  : { chosenFrame: picked.frame, statement: picked.statement, tenPercentTrue: picked.tenPercentTrue }
                : activeOperator === "name"
                ? { chosenName: freeText.trim() }
                : { chosenFrame: freeText.trim() }
            );
          }}
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  const renderSplitBoard = () => {
    const parts: any[] = assist?.members || assist?.parts || [];
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    return (
      <div className="space-y-2">
        {assist?.seam && (
          <p className="text-xs text-muted-foreground">
            {tm("split_seam", "Split along the seam of")}: <b>{assist.seam}</b>
          </p>
        )}
        {parts.map((p, i) => (
          <label
            key={i}
            className={cn(
              "flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer",
              selectedParts[i] ? "border-primary bg-primary/5" : "border-border"
            )}
          >
            <input
              type="checkbox"
              checked={Boolean(selectedParts[i])}
              onChange={(e) => setSelectedParts({ ...selectedParts, [i]: e.target.checked })}
              className="mt-0.5"
            />
            <span>
              {p.statement}
              {p.entryPoint && (
                <span className="ms-2 text-[10px] uppercase rounded bg-emerald-100 text-emerald-700 px-1.5 py-0.5">
                  {tm("entry_point", "start here")}
                </span>
              )}
              {p.note && <p className="text-xs text-muted-foreground mt-0.5">{p.note}</p>}
            </span>
          </label>
        ))}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={tm("add_own_parts", "Add your own parts, one per line…")}
          rows={2}
          className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
        />
        <Button
          disabled={
            applying ||
            (parts.filter((_, i) => selectedParts[i]).length === 0 && !freeText.trim())
          }
          onClick={() => {
            const kept = parts.filter((_, i) => selectedParts[i]);
            const own = freeText
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((statement) => ({ statement }));
            submit({ proposed: parts }, { seam: assist?.seam, members: [...kept, ...own] });
          }}
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  const renderFreetextGuided = () => {
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    const extraLines: string[] =
      activeOperator === "contextualize"
        ? assist?.originQuestions || []
        : activeOperator === "deduce"
        ? assist?.implications || []
        : activeOperator === "simulate"
        ? assist?.prompts || []
        : activeOperator === "own_disown"
        ? assist?.voiceQuestions || []
        : activeOperator === "amplify"
        ? assist?.anchors || []
        : activeOperator === "simplify"
        ? (assist?.dropped || []).map((d: string) => `(setting aside: ${d})`)
        : activeOperator === "retire" && assist?.successorPrompt
        ? [assist.successorPrompt]
        : [];

    const scaffold =
      activeOperator === "distance"
        ? assist?.defused
        : activeOperator === "doubt"
        ? assist?.loadBearing?.challenge
        : activeOperator === "question"
        ? (assist?.questions || []).map((q: any) => `• ${q.question}`).join("\n")
        : activeOperator === "bound"
        ? assist?.boundedRestatement
        : activeOperator === "concretize"
        ? (assist?.candidates || []).map((c: any) => `• ${c.concrete}`).join("\n")
        : [assist?.scaffold, ...extraLines.map((l: string) => `• ${l}`)]
            .filter(Boolean)
            .join("\n") || undefined;

    return (
      <div className="space-y-2">
        {assistLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {tm("thinking", "Preparing prompts…")}
          </div>
        )}
        {scaffold && (
          <div className="rounded-lg bg-muted/60 p-3 text-sm whitespace-pre-wrap">{scaffold}</div>
        )}
        {activeOperator === "doubt" && assist?.loadBearing?.premise && (
          <p className="text-xs text-muted-foreground">
            {tm("load_bearing", "Load-bearing premise")}: “{assist.loadBearing.premise}”
          </p>
        )}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={
            activeOperator === "park"
              ? tm("park_placeholder", "When should this come back? (a date or an event)")
              : tm("your_response", "Your response…")
          }
          rows={4}
          className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
          autoFocus
        />
        <Button
          disabled={applying || !freeText.trim()}
          onClick={() => {
            if (activeOperator === "park") {
              onPark(freeText.trim());
              reset();
              return;
            }
            if (activeOperator === "retire") {
              onRetire(freeText.trim());
              reset();
              return;
            }
            submit({ scaffold: assist || null }, { response: freeText.trim() });
          }}
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  const renderExternalCommit = () => (
    <div className="space-y-2">
      {assistLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {tm("designing_test", "Designing a probe…")}
        </div>
      )}
      {assist && (
        <div className="rounded-lg bg-muted/60 p-3 text-sm space-y-1">
          <p>
            <b>{tm("hypothesis", "Hypothesis")}:</b> {assist.hypothesis}
          </p>
          <p>
            <b>{tm("probe", "Probe")}:</b> {assist.probe}
          </p>
          <p className="text-amber-700">
            <b>{tm("lose_condition", "It loses if")}:</b> {assist.loseCondition}
          </p>
          {assist.deadlineDays && (
            <p className="text-xs text-muted-foreground">
              {tm("deadline", "Deadline")}: {assist.deadlineDays} {t("days", "days")}
            </p>
          )}
        </div>
      )}
      <textarea
        value={freeText}
        onChange={(e) => setFreeText(e.target.value)}
        placeholder={tm("adjust_probe", "Adjust the probe if needed, or write your own…")}
        rows={2}
        className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
      />
      <p className="text-xs text-muted-foreground">
        {tm("test_becomes_action", "Committing creates an action item in your workspace.")}
      </p>
      <Button
        disabled={applying || (!assist && !freeText.trim())}
        onClick={() =>
          submit(
            { assist },
            {
              hypothesis: assist?.hypothesis || object.statement,
              probe: freeText.trim() || assist?.probe,
              deadlineDays: assist?.deadlineDays || 14,
              loseCondition: assist?.loseCondition || freeText.trim(),
            }
          )
        }
      >
        {tm("op_test_done", "Commit to the test")}
      </Button>
    </div>
  );

  const renderCompareGrid = () => (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-medium text-muted-foreground">
          {tm("compare_target_label", "Compared against what, concretely?")}
        </label>
        <div className="flex items-center gap-2 mt-1">
          <input
            value={compareTarget}
            onChange={(e) => setCompareTarget(e.target.value)}
            placeholder={tm("compare_target_placeholder", "e.g. the actual offer from Acme")}
            className="flex-1 rounded-lg border border-border px-3 py-2 text-sm bg-background"
            autoFocus
          />
          <Button
            size="sm"
            variant="outline"
            disabled={assistLoading || !compareTarget.trim()}
            onClick={async () => {
              setAssistLoading(true);
              try {
                const res = await operatorAssist(retryFetch, {
                  operatorId: "compare",
                  fiObject: object._id,
                  language: i18n.language?.split("-")[0] || "en",
                  register,
                  extra: { target: compareTarget.trim() },
                });
                setAssist(res.assist);
                setCompareRows(
                  (res.assist.criteria || []).map((c: any) => ({ name: c.name, a: 3, b: 3 }))
                );
              } catch {
                setCompareRows([{ name: "", a: 3, b: 3 }]);
              } finally {
                setAssistLoading(false);
              }
            }}
          >
            {assistLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : tm("compare_suggest", "Suggest criteria")}
          </Button>
        </div>
      </div>
      {compareRows.length > 0 && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-[10px] uppercase tracking-wide text-muted-foreground px-1">
            <span>{tm("compare_criterion", "Criterion")}</span>
            <span className="w-14 text-center">{tm("compare_this", "This")}</span>
            <span className="w-14 text-center">{tm("compare_that", "That")}</span>
          </div>
          {compareRows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
              <input
                value={row.name}
                onChange={(e) => {
                  const next = [...compareRows];
                  next[i] = { ...row, name: e.target.value };
                  setCompareRows(next);
                }}
                className="rounded-lg border border-border px-2.5 py-1.5 text-xs bg-white"
              />
              {(["a", "b"] as const).map((side) => (
                <select
                  key={side}
                  value={row[side]}
                  onChange={(e) => {
                    const next = [...compareRows];
                    next[i] = { ...row, [side]: Number(e.target.value) };
                    setCompareRows(next);
                  }}
                  className="w-14 rounded-lg border border-border px-1 py-1.5 text-xs bg-white text-center"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              ))}
            </div>
          ))}
          <button
            onClick={() => setCompareRows([...compareRows, { name: "", a: 3, b: 3 }])}
            className="text-[11px] text-primary/70 hover:text-primary cursor-pointer"
          >
            + {tm("compare_add_criterion", "add a criterion")}
          </button>
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder={tm("compare_verdict_placeholder", "What does the comparison actually say?")}
            rows={2}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
          />
          <Button
            disabled={applying || compareRows.every((r) => !r.name.trim())}
            onClick={() =>
              submit(
                { criteria: assist?.criteria || null },
                {
                  target: compareTarget.trim(),
                  criteria: compareRows.filter((r) => r.name.trim()),
                  response: freeText.trim() || undefined,
                }
              )
            }
          >
            {tm("op_compare_done", "Save the comparison")}
          </Button>
        </div>
      )}
    </div>
  );

  const renderLinkPicker = () => (
    <div className="space-y-3">
      {otherObjects.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {tm("link_no_objects", "No other thoughts to connect yet — capture another one first.")}
        </p>
      ) : (
        <>
          <div className="space-y-1 max-h-44 overflow-y-auto pe-1">
            {otherObjects.map((o) => (
              <button
                key={o._id}
                onClick={() => setSelectedLinkTarget(o._id)}
                className={cn(
                  "w-full text-start rounded-lg border px-3 py-2 text-xs cursor-pointer transition-colors duration-150",
                  selectedLinkTarget === o._id
                    ? "border-primary bg-primary/[0.06]"
                    : "border-border/70 bg-white hover:border-primary/30"
                )}
              >
                <span className="line-clamp-1">{o.label || o.workingStatement || o.statement}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {LINK_TYPES.map((lt) => (
              <button
                key={lt}
                onClick={() => setSelectedLinkType(lt)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] cursor-pointer transition-colors duration-150",
                  selectedLinkType === lt
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border/80 text-muted-foreground hover:text-foreground"
                )}
              >
                {tm(`link_type_${lt}`, lt.replace(/_/g, " "))}
              </button>
            ))}
          </div>
          <input
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder={tm("link_note_placeholder", "Optional note about this connection…")}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
          />
          <Button
            disabled={applying || !selectedLinkTarget || !LINK_TYPES.includes(selectedLinkType as any)}
            onClick={() => {
              const target = otherObjects.find((o) => o._id === selectedLinkTarget);
              submit(
                {},
                {
                  fiObject: selectedLinkTarget,
                  linkType: selectedLinkType,
                  targetStatement: target ? (target.label || target.statement).slice(0, 200) : undefined,
                  note: freeText.trim() || undefined,
                }
              );
            }}
          >
            {tm("op_link_done", "Create the link")}
          </Button>
        </>
      )}
    </div>
  );

  const renderTerritoryMap = () => {
    const linked = (object.links || [])
      .map((l) => ({
        ...l,
        target: otherObjects.find((o) => o._id === l.fiObject),
      }))
      .filter((l) => l.target)
      .slice(0, 8);
    const cx = 150;
    const cy = 110;
    const r = 78;
    return (
      <div className="space-y-3">
        {linked.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {tm("map_no_links", "This thought has no connections yet — use “Connect it” first, then map the territory.")}
          </p>
        ) : (
          <div className="rounded-xl border border-border/60 bg-white p-2 overflow-x-auto">
            <svg viewBox="0 0 300 220" className="w-full max-w-[340px] mx-auto">
              {linked.map((l, i) => {
                const angle = (i / linked.length) * 2 * Math.PI - Math.PI / 2;
                const x = cx + r * Math.cos(angle);
                const y = cy + r * Math.sin(angle);
                const label = (l.target!.label || l.target!.statement).slice(0, 22);
                return (
                  <g key={i}>
                    <line x1={cx} y1={cy} x2={x} y2={y} className="stroke-border" strokeWidth={1} />
                    <text
                      x={(cx + x) / 2}
                      y={(cy + y) / 2 - 3}
                      className="fill-slate-400"
                      fontSize={6.5}
                      textAnchor="middle"
                    >
                      {l.linkType.replace(/_/g, " ")}
                    </text>
                    <circle cx={x} cy={y} r={17} className="fill-muted stroke-border" strokeWidth={1} />
                    <text x={x} y={y + 2} fontSize={6.5} textAnchor="middle" className="fill-slate-600">
                      {label.length > 14 ? label.slice(0, 13) + "…" : label}
                    </text>
                  </g>
                );
              })}
              <circle cx={cx} cy={cy} r={24} className="fill-primary/10 stroke-primary/50" strokeWidth={1.5} />
              <text x={cx} y={cy + 2} fontSize={7} textAnchor="middle" className="fill-primary font-medium">
                {(object.label || object.statement).slice(0, 16)}
              </text>
            </svg>
          </div>
        )}
        {assistLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> {tm("thinking", "Preparing prompts…")}
          </div>
        )}
        {assist && (
          <div className="rounded-lg bg-muted/60 p-3 text-xs space-y-1">
            {assist.chokepoint && (
              <p><b>{tm("map_chokepoint", "Chokepoint")}:</b> {assist.chokepoint}</p>
            )}
            {assist.gap && <p><b>{tm("map_gap", "Missing")}:</b> {assist.gap}</p>}
            {assist.question && <p className="italic">{assist.question}</p>}
          </div>
        )}
        {linked.length > 0 && (
          <>
            <textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder={tm("map_response_placeholder", "What does the territory tell you?")}
              rows={3}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background"
            />
            <Button
              disabled={applying || !freeText.trim()}
              onClick={() =>
                submit(
                  { chokepoint: assist?.chokepoint || null, gap: assist?.gap || null },
                  { response: freeText.trim() }
                )
              }
            >
              {tm("op_map_done", "Save what you see")}
            </Button>
          </>
        )}
      </div>
    );
  };

  // ----- design-family archetypes (ICDF §3) ---------------------------------

  /** AI proposals as a keep/edit/extend board — criteria, options, patches, causes. */
  const renderChecklistBoard = () => {
    const cfg = CHECKLIST_CONFIG[activeOperator!];
    const items: any[] = (assist && assist[cfg.assistKey]) || [];
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    const keptCount = items.filter((_, i) => selectedParts[i]).length;
    return (
      <div className="space-y-2">
        {items.map((item, i) => (
          <label
            key={i}
            className={cn(
              "flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer transition-colors duration-150",
              selectedParts[i] ? "border-primary bg-primary/5" : "border-border"
            )}
          >
            <input
              type="checkbox"
              checked={Boolean(selectedParts[i])}
              onChange={(e) => setSelectedParts({ ...selectedParts, [i]: e.target.checked })}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="font-medium">{item[cfg.primary]}</span>
              {cfg.secondary && item[cfg.secondary] && (
                <p className="text-xs text-muted-foreground mt-0.5">{item[cfg.secondary]}</p>
              )}
              {cfg.tertiary && item[cfg.tertiary] && (
                <p className="text-xs text-amber-700 mt-0.5">
                  {tm("tradeoff", "Tradeoff:")} {item[cfg.tertiary]}
                </p>
              )}
            </span>
          </label>
        ))}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={tm("checklist_add_own", "Add your own, one per line…")}
          rows={2}
          className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
        />
        <Button
          disabled={applying || (keptCount === 0 && !freeText.trim())}
          onClick={() => {
            const kept = items.filter((_, i) => selectedParts[i]);
            const own = freeText
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((text) => ({ [cfg.primary]: text }));
            submit({ proposed: items }, { [cfg.outputKey]: [...kept, ...own] });
          }}
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  /** AI questions, one answer field each — research unknowns, test-run scenes. */
  const renderGuidedSteps = () => {
    const cfg = GUIDED_STEPS_CONFIG[activeOperator!];
    const items: any[] = (assist && assist[cfg.assistKey]) || [];
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    const knowns: string[] = (activeOperator === "dsn_research" && assist?.knowns) || [];
    const answered = items.filter((_, i) => (stepAnswers[i] || "").trim());
    if (!items.length) return renderFreetextGuided();
    return (
      <div className="space-y-2.5">
        {knowns.length > 0 && (
          <div className="rounded-lg bg-muted/60 p-3 text-xs space-y-1">
            <p className="font-medium text-muted-foreground uppercase tracking-wide text-[10px]">
              {tm("research_knowns", "Already known")}
            </p>
            {knowns.map((k, i) => (
              <p key={i}>• {k}</p>
            ))}
          </div>
        )}
        {items.map((item, i) => (
          <div key={i} className="rounded-lg border border-border/80 bg-white p-3 space-y-1.5">
            <p className="text-sm font-medium">{item[cfg.prompt]}</p>
            {cfg.sub && item[cfg.sub] && (
              <p className="text-xs text-muted-foreground">{item[cfg.sub]}</p>
            )}
            <input
              value={stepAnswers[i] || ""}
              onChange={(e) => setStepAnswers({ ...stepAnswers, [i]: e.target.value })}
              placeholder={
                activeOperator === "dsn_mental_test"
                  ? tm("steps_creak_placeholder", "What creaks here? (leave empty if it holds)")
                  : tm("steps_answer_placeholder", "What you know or found…")
              }
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
            />
          </div>
        ))}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={tm("steps_extra_placeholder", "Anything else you noticed… (optional)")}
          rows={2}
          className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
        />
        <Button
          disabled={applying || (answered.length === 0 && !freeText.trim())}
          onClick={() => {
            const results = items
              .map((item, i) => ({
                [cfg.promptOutKey]: item[cfg.prompt],
                [cfg.answerKey]: (stepAnswers[i] || "").trim(),
              }))
              .filter((r: any) => r[cfg.answerKey]);
            submit(
              { proposed: items },
              { [cfg.outputKey]: results, response: freeText.trim() || undefined }
            );
          }}
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  /** Two framing fields (problem / done-means) with AI suggestion chips. */
  const renderDefineFrame = () => {
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    const field = (
      label: string,
      value: string,
      setValue: (v: string) => void,
      candidates: string[],
      placeholder: string
    ) => (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
        />
        {candidates.length > 0 && (
          <div className="flex flex-col gap-1">
            {candidates.map((c, i) => (
              <button
                key={i}
                onClick={() => setValue(c)}
                className={cn(
                  "text-start rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition-colors duration-150",
                  value === c
                    ? "border-primary bg-primary/[0.06] text-foreground"
                    : "border-border/70 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    );
    return (
      <div className="space-y-3">
        {field(
          tm("define_problem_label", "The problem, in one sentence"),
          defineProblem,
          setDefineProblem,
          assist?.problemCandidates || [],
          tm("define_problem_placeholder", "What's actually wrong here…")
        )}
        {field(
          tm("define_done_label", "'Done' means…"),
          defineDone,
          setDefineDone,
          assist?.doneCandidates || [],
          tm("define_done_placeholder", "What you'd concretely observe once it's solved…")
        )}
        <Button
          disabled={applying || !defineProblem.trim() || !defineDone.trim()}
          onClick={() =>
            submit(
              {
                problemCandidates: assist?.problemCandidates || null,
                doneCandidates: assist?.doneCandidates || null,
              },
              { problem: defineProblem.trim(), doneMeans: defineDone.trim() }
            )
          }
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  /** Options × criteria scoring grid + the synthesis that steals the best parts. */
  const renderDesignMatrix = () => {
    const done = OPERATOR_FALLBACKS[activeOperator!]?.done || "Apply";
    const options: string[] = (assist?.options || []).slice(0, 3);
    const criteria: string[] = (assist?.criteria || []).slice(0, 5);
    const score = (c: string, o: string) => matrixScores[c]?.[o] ?? 3;
    return (
      <div className="space-y-3">
        {options.length > 0 && criteria.length > 0 && (
          <div className="overflow-x-auto">
            <div
              className="grid gap-x-2 gap-y-1.5 items-center min-w-[280px]"
              style={{ gridTemplateColumns: `minmax(90px,1fr) repeat(${options.length}, auto)` }}
            >
              <span />
              {options.map((o) => (
                <span
                  key={o}
                  className="text-[10px] uppercase tracking-wide text-muted-foreground text-center max-w-[80px] truncate"
                  title={o}
                >
                  {o}
                </span>
              ))}
              {criteria.map((c) => (
                <React.Fragment key={c}>
                  <span className="text-xs truncate" title={c}>
                    {c}
                  </span>
                  {options.map((o) => (
                    <select
                      key={o}
                      value={score(c, o)}
                      onChange={(e) =>
                        setMatrixScores({
                          ...matrixScores,
                          [c]: { ...(matrixScores[c] || {}), [o]: Number(e.target.value) },
                        })
                      }
                      className="w-12 rounded-lg border border-border px-1 py-1 text-xs bg-white text-center"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        {assist?.hint && (
          <p className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">{assist.hint}</p>
        )}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={tm(
            "synthesize_placeholder",
            "The combined design: what it takes from each option…"
          )}
          rows={3}
          className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
        />
        <Button
          disabled={applying || !freeText.trim()}
          onClick={() => {
            const scores: Record<string, Record<string, number>> = {};
            criteria.forEach((c) => {
              scores[c] = {};
              options.forEach((o) => (scores[c][o] = score(c, o)));
            });
            submit(
              { options, criteria, hint: assist?.hint || null },
              { options, criteria, scores, synthesis: freeText.trim() }
            );
          }}
        >
          {tm(`op_${activeOperator}_done`, done)}
        </Button>
      </div>
    );
  };

  /** The design leaves the head: first step + moment + holding sign → action item. */
  const renderDesignCommit = () => (
    <div className="space-y-2">
      {assist && (
        <div className="rounded-lg bg-muted/60 p-3 text-sm space-y-1">
          <p>
            <b>{tm("commit_first_step", "First step")}:</b> {assist.firstStep}
          </p>
          {assist.when && (
            <p>
              <b>{tm("commit_when", "When")}:</b> {assist.when}
            </p>
          )}
          {assist.successSign && (
            <p className="text-emerald-700">
              <b>{tm("commit_success_sign", "It's holding if")}:</b> {assist.successSign}
            </p>
          )}
        </div>
      )}
      <input
        value={freeText}
        onChange={(e) => setFreeText(e.target.value)}
        placeholder={tm("commit_step_placeholder", "Adjust the first step if needed, or write your own…")}
        className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
      />
      <input
        value={commitWhen}
        onChange={(e) => setCommitWhen(e.target.value)}
        placeholder={tm("commit_when_placeholder", "When will you do it? (optional)")}
        className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
      />
      <p className="text-xs text-muted-foreground">
        {tm("test_becomes_action", "Committing creates an action item in your workspace.")}
      </p>
      <Button
        disabled={applying || (!assist?.firstStep && !freeText.trim())}
        onClick={() =>
          submit(
            { assist },
            {
              firstStep: freeText.trim() || assist?.firstStep,
              when: commitWhen.trim() || assist?.when || undefined,
              deadlineDays: assist?.deadlineDays || 7,
              successSign: assist?.successSign || undefined,
            }
          )
        }
      >
        {tm("op_dsn_implement_done", "Commit")}
      </Button>
    </div>
  );

  const renderActive = () => {
    const meta = OPERATOR_META[activeOperator!];
    if (!meta) return null;
    const aiFirstArchetypes = [
      "generate_choose",
      "checklist_board",
      "guided_steps",
      "define_frame",
      "design_matrix",
    ];
    if (assistLoading && aiFirstArchetypes.includes(meta.interaction)) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          {tm("generating", "Generating options…")}
        </div>
      );
    }
    switch (meta.interaction) {
      case "generate_choose":
        return renderGenerateChoose();
      case "split_board":
        return renderSplitBoard();
      case "external_commit":
        return renderExternalCommit();
      case "compare_grid":
        return renderCompareGrid();
      case "link_picker":
        return renderLinkPicker();
      case "territory_map":
        return renderTerritoryMap();
      case "checklist_board":
        return assist ? renderChecklistBoard() : renderFreetextGuided();
      case "guided_steps":
        return renderGuidedSteps();
      case "define_frame":
        return renderDefineFrame();
      case "design_matrix":
        return renderDesignMatrix();
      case "design_commit":
        return renderDesignCommit();
      default:
        return renderFreetextGuided();
    }
  };

  // ----- menu ------------------------------------------------------------------

  const routeIds = (plannedRoute || []).filter((s) => s.status === "pending").map((s) => s.operatorId);
  const pathIds = routeIds.filter((id) => menu.allowed.includes(id) && OPERATOR_FALLBACKS[id]);
  let moreIds = menu.allowed.filter((id) => !routeIds.includes(id) && OPERATOR_FALLBACKS[id]);
  // ICDF §4 — encouraging new operators: reserve the first "More moves" slot
  // for one move the user has never tried (already state-relevant, since the
  // menu only contains applicable operators).
  const exploreId =
    newOperatorIds && moreIds.length > 1
      ? moreIds.find((id) => newOperatorIds.includes(id))
      : undefined;
  if (exploreId) {
    moreIds = [exploreId, ...moreIds.filter((id) => id !== exploreId)];
  }

  const renderPathCard = (id: string, index: number) => {
    const fb = OPERATOR_FALLBACKS[id];
    const meta = OPERATOR_META[id];
    const visual = OPERATOR_VISUALS[id];
    const Icon = visual?.icon;
    const tint = FAMILY_TINTS[meta?.family] || { bg: "bg-muted", fg: "text-muted-foreground" };
    const isNext = index === 0 || menu.forcedFirst === id;
    return (
      <button
        key={id}
        onClick={() => startOperator(id)}
        className={cn(
          "group w-full flex items-start gap-3 rounded-xl border p-3.5 text-start min-h-[44px] cursor-pointer",
          "transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          isNext
            ? "border-primary/50 bg-primary/[0.05] shadow-sm ring-1 ring-primary/20"
            : "border-border/80 bg-white hover:border-primary/30 hover:shadow-sm"
        )}
      >
        <span className="relative flex-shrink-0">
          <span className={cn("flex items-center justify-center h-9 w-9 rounded-xl", tint.bg)}>
            {Icon && <Icon className={cn("w-4.5 h-4.5 w-[18px] h-[18px]", tint.fg)} />}
          </span>
          {index >= 0 && (
            <span className="absolute -top-1.5 -start-1.5 flex items-center justify-center min-h-[18px] min-w-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-semibold tabular-nums">
              {index + 1}
            </span>
          )}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium">
            {tm(`op_${id}_title`, fb.title)}
            {meta?.aiAssist && <Sparkles className="w-3 h-3 text-violet-400 opacity-70" />}
            {newOperatorIds?.includes(id) && (
              <span className="text-[10px] rounded-full bg-indigo-50 text-indigo-600 px-1.5 py-0.5 flex-shrink-0">
                {tm("try_new", "new to you")}
              </span>
            )}
            {isNext && (
              <span className="ms-auto text-[10px] uppercase tracking-wide rounded-full bg-primary text-primary-foreground px-2 py-0.5 flex-shrink-0">
                {tm("suggested", "up next")}
              </span>
            )}
          </span>
          {register !== "plain" && (
            <span className="block text-xs text-muted-foreground mt-1 leading-relaxed">
              {tm(`op_${id}_help`, fb.help)}
            </span>
          )}
          {visual && (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              <span className="text-[11px] italic text-muted-foreground/80">
                {tm(`op_${id}_example`, `e.g. ${visual.example}`)}
              </span>
              <span className="text-[10px] uppercase tracking-wide rounded-full bg-muted/70 text-muted-foreground px-2 py-0.5">
                {tm(`op_${id}_effect`, visual.effect)}
              </span>
            </span>
          )}
        </span>
      </button>
    );
  };

  const renderCompactCard = (id: string, isExploreSlot?: boolean) => {
    const fb = OPERATOR_FALLBACKS[id];
    const meta = OPERATOR_META[id];
    const visual = OPERATOR_VISUALS[id];
    const Icon = visual?.icon;
    const tint = FAMILY_TINTS[meta?.family] || { bg: "bg-muted", fg: "text-muted-foreground" };
    return (
      <button
        key={id}
        onClick={() => startOperator(id)}
        title={tm(`op_${id}_help`, fb.help)}
        className={cn(
          "flex items-center gap-2.5 rounded-xl border bg-white p-2.5 text-start min-h-[44px] cursor-pointer",
          "transition-all duration-150 hover:border-primary/30 hover:shadow-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          isExploreSlot ? "border-indigo-200 bg-indigo-50/40" : "border-border/70"
        )}
      >
        <span className={cn("flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0", tint.bg)}>
          {Icon && <Icon className={cn("w-4 h-4", tint.fg)} />}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-1 text-xs font-medium truncate">
            {tm(`op_${id}_title`, fb.title)}
            {meta?.aiAssist && <Sparkles className="w-2.5 h-2.5 text-violet-400 flex-shrink-0" />}
            {newOperatorIds?.includes(id) && (
              <span className="text-[9px] rounded-full bg-indigo-50 text-indigo-600 px-1.5 flex-shrink-0">
                {tm("try_new", "new to you")}
              </span>
            )}
          </span>
          {visual && (
            <span className="block text-[10px] text-muted-foreground truncate mt-0.5">
              {tm(`op_${id}_effect`, visual.effect)}
            </span>
          )}
        </span>
      </button>
    );
  };

  // Model page: one suggested move, the rest behind a disclosure
  const allIds = [...pathIds, ...moreIds];
  const suggestedId =
    menu.forcedFirst && allIds.includes(menu.forcedFirst) ? menu.forcedFirst : allIds[0];
  const otherIds = allIds.filter((id) => id !== suggestedId);

  const renderMoveCard = (id: string, prominent: boolean) => {
    const fb = OPERATOR_FALLBACKS[id];
    const meta = OPERATOR_META[id];
    const visual = OPERATOR_VISUALS[id];
    const Icon = visual?.icon;
    const tint = FAMILY_TINTS[meta?.family] || { bg: "bg-muted", fg: "text-muted-foreground" };
    return (
      <button
        key={id}
        type="button"
        onClick={() => startOperator(id)}
        data-testid={prominent ? "mm-suggested-move" : undefined}
        className={cn(
          "group w-full flex items-start gap-3 rounded-2xl border text-start min-h-[44px] cursor-pointer",
          "transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          prominent
            ? "border-primary/40 bg-primary/[0.05] p-4 hover:border-primary/60"
            : "border-border bg-card p-3 hover:border-primary/30"
        )}
      >
        <span className={cn("flex items-center justify-center h-9 w-9 rounded-xl flex-none", tint.bg)}>
          {Icon && <Icon className={cn("w-[18px] h-[18px]", tint.fg)} />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            {tm(`op_${id}_title`, fb.title)}
            {meta?.aiAssist && <Sparkles className="w-3 h-3 text-violet-400 opacity-70" />}
          </span>
          <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {tm(`op_${id}_help`, fb.help)}
          </span>
          {visual && (
            <span className="block text-xs text-primary/80 mt-1">{tm(`op_${id}_effect`, visual.effect)}</span>
          )}
          {!prominent && (visual || newOperatorIds?.includes(id)) && (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              {newOperatorIds?.includes(id) && (
                <span className="text-[10px] rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300 px-1.5 py-0.5">
                  {tm("try_new", "new to you")}
                </span>
              )}
              {visual && (
                <span className="text-[11px] italic text-muted-foreground/80">
                  {tm(`op_${id}_example`, `e.g. ${visual.example}`)}
                </span>
              )}
            </span>
          )}
        </span>
      </button>
    );
  };

  const renderPageMenu = () => (
    <div className="space-y-3">
      {suggestedId && renderMoveCard(suggestedId, true)}
      {otherIds.length > 0 && (
        <div>
          <button
            type="button"
            aria-expanded={otherMovesOpen}
            onClick={() => setOtherMovesOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-2 -ms-2 min-h-[44px] text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 motion-safe:transition-transform motion-safe:duration-200",
                otherMovesOpen && "rotate-180"
              )}
            />
            {tm("other_moves", "Other moves ({{count}})").replace("{{count}}", String(otherIds.length))}
          </button>
          {otherMovesOpen && (
            <div className="mt-2 space-y-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200">
              {otherIds.map((id) => renderMoveCard(id, false))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {pageLayout && warnings.includes("R1_distance_first") && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground mb-3">
          <Shield className="w-4 h-4 mt-0.5 flex-none text-primary/60" />
          {tm(
            "warning_r1",
            "This feels heavy and close right now. Let's step back from it first — other moves unlock after."
          )}
        </p>
      )}
      {!pageLayout && warnings.includes("R1_distance_first") && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3 text-sm text-amber-800">
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {tm(
            "warning_r1",
            "This feels heavy and close right now. Let's step back from it first — other moves unlock after."
          )}
        </div>
      )}
      {warnings.includes("R2_examine_before_reframe") && !activeOperator && (
        <p className="text-xs text-muted-foreground mb-2">
          {tm("warning_r2", "This is held with high certainty but hasn't been examined — examine before reframing.")}
        </p>
      )}

      {!activeOperator ? hideMenu ? null : pageLayout ? (
        renderPageMenu()
      ) : (
        <div className="space-y-4">
          {pathIds.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                {tm("your_path", "Your path")}
              </p>
              <div className="space-y-2">{pathIds.map((id, i) => renderPathCard(id, i))}</div>
            </div>
          )}
          {moreIds.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                {pathIds.length > 0 ? tm("more_moves", "More moves") : tm("all_moves", "Choose a move")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {moreIds.map((id) =>
                  pathIds.length > 0 ? renderCompactCard(id, id === exploreId) : renderPathCard(id, -1)
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border/80 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  FAMILY_ACCENTS[OPERATOR_META[activeOperator]?.family] || "bg-slate-300"
                )}
              />
              {tm(`op_${activeOperator}_title`, OPERATOR_FALLBACKS[activeOperator]?.title || activeOperator)}
            </h4>
            <button
              onClick={reset}
              className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150 px-2 py-1 -me-2"
            >
              {t("cancel", "Cancel")}
            </button>
          </div>
          {register !== "plain" && (
            <p className="text-xs text-muted-foreground mb-3 ps-3.5 leading-relaxed">
              {tm(`op_${activeOperator}_help`, OPERATOR_FALLBACKS[activeOperator]?.help || "")}
            </p>
          )}
          {renderActive()}
        </div>
      )}
    </div>
  );
};

export default MmOperatorFlow;
